import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { stations as mockStations, formatMoneyShort, OwnerStation, ownerStatusColor, shortStationName } from '@/mocks/ownerDashboard';

interface PortfolioTilesSectionProps {
  onStationClick: (station: OwnerStation) => void;
  compact?: boolean;
  stations?: OwnerStation[];
  /** Hide money fields, show generation instead (dispatcher dashboard). */
  hideFinance?: boolean;
}

const statusColor = (s: OwnerStation): string => ownerStatusColor(s.status);
const fmtPct = (p: number | null | undefined) => (p == null ? '—' : `${p}%`);

export default function PortfolioTilesSection({ onStationClick, compact = false, stations: liveStations, hideFinance }: PortfolioTilesSectionProps) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const stations = liveStations && liveStations.length ? liveStations : mockStations;

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';

  if (compact) {
    return (
      <div
        className="rounded-xl p-4 flex flex-col h-full"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
      >
        <h3 className="text-sm font-semibold mb-3 shrink-0" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
          {t('d_port_title')}
        </h3>
        <div className="flex flex-col gap-2 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
          {stations.map((s) => {
            const sc = statusColor(s);
            const pre = s.status === 'pre_launch';
            return (
              <button
                key={s.id}
                onClick={() => onStationClick(s)}
                title={`${t('d_port_open')}: ${s.name}`}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-left cursor-pointer transition-all duration-200 hover:brightness-110 w-full"
                style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7' }}
              >
                <span className="rounded-full shrink-0" style={{ width: 9, height: 9, background: sc }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold truncate" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {shortStationName(s.name)}
                  </div>
                  <div className="text-[10px] truncate" style={{ color: sc, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {s.installedMw} МВт · {s.statusLabel}{s.statusNote ? ` (${s.statusNote})` : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-bold tabular-nums leading-tight" style={{ color: sc, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {pre ? '—' : fmtPct(s.percentOfPotential)}
                  </div>
                  <div className="text-[9px] leading-none" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {pre ? '' : t('d_port_fc_eff')}
                  </div>
                  <div className="text-[11px] font-semibold tabular-nums mt-1 leading-tight" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {pre ? '—' : (hideFinance
                      ? (s.todayGenMwh != null ? `${s.todayGenMwh.toFixed(1)} МВт·год` : '—')
                      : `${formatMoneyShort(s.todayRevenueUah)} грн`)}
                  </div>
                  <div className="text-[9px] leading-none" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                    {pre ? '' : (hideFinance ? t('d_port_gen_today') : t('d_port_rev_day'))}
                  </div>
                </div>
                <i className="ri-arrow-right-s-line shrink-0" style={{ color: textDim }} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5">
      <h3 className="text-sm font-semibold mb-3" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
        Станції портфеля
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {stations.map((s) => {
          const sc = statusColor(s);
          return (
            <button
              key={s.id}
              onClick={() => onStationClick(s)}
              className="rounded-xl p-4 text-left cursor-pointer transition-all duration-200 hover:-translate-y-0.5 w-full"
              style={{
                background: cardBg,
                border: `1px solid ${cardBorder}`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="rounded-full shrink-0" style={{ width: 8, height: 8, background: sc }} />
                <span
                  className="text-sm font-semibold truncate"
                  style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}
                >
                  {shortStationName(s.name)}
                </span>
              </div>
              <div
                className="text-base font-bold tabular-nums mb-1"
                style={{ color: sc, fontFamily: 'Muller, Inter, sans-serif', letterSpacing: '-0.02em' }}
              >
                {s.status === 'pre_launch' ? t('d_port_not_launched') : fmtPct(s.percentOfPotential)}
              </div>
              <div className="text-[11px] mb-2" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>
                {s.status === 'pre_launch' ? s.statusNote : (s.percentOfPotential == null ? t('d_map_no_telemetry') : t('d_port_fc_now'))}
              </div>
              <div className="text-xs mb-2" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                {t('d_port_guar_rev_today')}: {formatMoneyShort(s.todayRevenueUah)} {t('d_u_uah')}
              </div>
              <div className="w-full h-1 rounded-full mb-2" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F5' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${s.percentOfPotential ?? 0}%`,
                    background: sc,
                  }}
                />
              </div>
              <div className="text-[10px]" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                {t('d_port_installed')} {s.installedMw} {t('d_u_mw')}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}